import { useEffect, useState, useCallback } from "react";
import { api, pageImageUrl, formatApiErrorDetail } from "@/lib/apiClient";
import { AdminTabs } from "@/components/AdminTabs";
import { toast } from "sonner";
import { UploadCloud, Trash2, Plus, BookOpen } from "lucide-react";

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [pageNumber, setPageNumber] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkStart, setBulkStart] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/pages");
    setPages(data);
    setPageNumber((prev) => prev || (data.length ? String(Math.max(...data.map((p) => p.page_number)) + 1) : "1"));
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (f) setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a page image.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("page_number", parseInt(pageNumber, 10));
      form.append("caption", caption);
      form.append("file", file);
      await api.post("/pages", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Page added to the volume.");
      setCaption("");
      setFile(null);
      setPreview(null);
      setPageNumber("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/pages/${id}`);
      setPages((p) => p.filter((x) => x.id !== id));
      toast.success("Page removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    if (!bulkFiles.length) {
      toast.error("Please select one or more page images.");
      return;
    }
    setBulkBusy(true);
    try {
      const form = new FormData();
      form.append("start_page_number", parseInt(bulkStart || "1", 10));
      bulkFiles.forEach((f) => form.append("files", f));
      const { data } = await api.post("/pages/bulk", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.count} page${data.count === 1 ? "" : "s"} added to the volume.`);
      setBulkFiles([]);
      setBulkStart("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <AdminTabs />

      <div className="mb-12">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-oxblood mb-4 flex items-center gap-2">
          <BookOpen strokeWidth={1.5} className="w-4 h-4" /> The UGRR Volume · 1872
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight leading-none">Book Pages</h1>
        <p className="font-body text-text-muted mt-5 max-w-2xl leading-relaxed">
          Upload scanned photos of the book's pages. These appear in the members-only Pages gallery,
          ordered by page number.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-12">
        <div className="space-y-8 self-start">
        <form onSubmit={submit} className="bg-surface border border-archive-border offset-shadow p-8 space-y-6">
          <div className="flex items-center gap-2 font-serif text-2xl text-ink">
            <Plus strokeWidth={1.5} className="w-5 h-5 text-oxblood" /> Add a Page
          </div>

          <div>
            <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
              Page Number
            </label>
            <input
              type="number"
              min="1"
              required
              value={pageNumber}
              onChange={(e) => setPageNumber(e.target.value)}
              data-testid="adminpages-number"
              className="w-full bg-parchment border border-archive-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-oxblood"
            />
          </div>

          <div>
            <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
              Caption
            </label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="adminpages-caption"
              placeholder="Optional note about this page"
              className="w-full bg-parchment border border-archive-border px-4 py-3 font-body text-ink focus:outline-none focus:ring-2 focus:ring-oxblood"
            />
          </div>

          <div>
            <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
              Page Image
            </label>
            <label className="flex flex-col items-center justify-center gap-3 border border-dashed border-archive-border bg-parchment px-4 py-8 cursor-pointer hover:border-oxblood transition-colors">
              {preview ? (
                <img src={preview} alt="preview" className="max-h-48 object-contain" />
              ) : (
                <>
                  <UploadCloud strokeWidth={1.5} className="w-8 h-8 text-oxblood" />
                  <span className="font-mono text-xs tracking-widest uppercase text-text-muted">
                    Choose an image (jpg, png)
                  </span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={onFile}
                data-testid="adminpages-file-input"
                className="hidden"
              />
            </label>
            {file && (
              <div className="font-mono text-[10px] tracking-widest uppercase text-text-muted mt-2">
                {file.name}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="adminpages-upload-button"
            className="w-full bg-oxblood text-parchment py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors disabled:opacity-60"
          >
            {busy ? "Uploading…" : "Add Page to Volume"}
          </button>
        </form>

        <form onSubmit={submitBulk} className="bg-surface border border-archive-border offset-shadow p-8 space-y-6">
          <div className="flex items-center gap-2 font-serif text-2xl text-ink">
            <UploadCloud strokeWidth={1.5} className="w-5 h-5 text-oxblood" /> Bulk Upload
          </div>
          <p className="font-body text-sm text-text-muted -mt-2">
            Drop several page photos at once — they'll be numbered in order, starting from the
            number below. Select them in page order.
          </p>

          <div>
            <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
              Start Page Number
            </label>
            <input
              type="number"
              min="1"
              value={bulkStart}
              onChange={(e) => setBulkStart(e.target.value)}
              data-testid="adminpages-bulk-start"
              placeholder={pageNumber || "1"}
              className="w-full bg-parchment border border-archive-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-oxblood"
            />
          </div>

          <div>
            <label className="flex flex-col items-center justify-center gap-3 border border-dashed border-archive-border bg-parchment px-4 py-8 cursor-pointer hover:border-oxblood transition-colors">
              <UploadCloud strokeWidth={1.5} className="w-8 h-8 text-oxblood" />
              <span className="font-mono text-xs tracking-widest uppercase text-text-muted text-center">
                {bulkFiles.length ? `${bulkFiles.length} images selected` : "Choose multiple images"}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
                data-testid="adminpages-bulk-input"
                className="hidden"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={bulkBusy}
            data-testid="adminpages-bulk-button"
            className="w-full bg-ink text-parchment py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood transition-colors disabled:opacity-60"
          >
            {bulkBusy ? "Uploading…" : `Upload ${bulkFiles.length || ""} Pages`}
          </button>
        </form>
        </div>

        <div>
          <h2 className="font-serif text-2xl text-ink mb-6">
            Pages in the Volume{pages.length ? ` (${pages.length})` : ""}
          </h2>
          {pages.length === 0 ? (
            <p className="font-body italic text-text-muted">No pages uploaded yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {pages.map((p) => (
                <div
                  key={p.id}
                  data-testid={`adminpages-row-${p.page_number}`}
                  className="bg-surface border border-archive-border p-2 relative group"
                >
                  <img
                    src={pageImageUrl(p.id)}
                    alt={`Page ${p.page_number}`}
                    className="w-full aspect-[3/4] object-cover border border-archive-border"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-mono text-[10px] tracking-widest uppercase text-oxblood">
                      p. {p.page_number}
                    </span>
                    <button
                      onClick={() => remove(p.id)}
                      data-testid={`adminpages-delete-${p.page_number}`}
                      className="text-text-muted hover:text-oxblood transition-colors"
                      title="Remove page"
                    >
                      <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
